import { isAxiosError } from "axios"
import { api } from "../../../services/authService"
import { OTPChannel, OTPPurpose } from "../../../services/otpService"

export type UpdateAgentRequest = {
	firstName?: string
	lastName?: string
	espressoLanguage?: string
	reAcceptTerms?: boolean
	agencyRole?: string
	agencyBusinessModel?: string
	jobTitle?: string
	yearsIndustry?: string
	countryCode?: string
	zipCode?: string
	consortialOrHost?: boolean
	consortialOrHostName?: string
	cliaAgentId?: string
	colonialID?: string
	regions?: string[]
	secondSpecialization?: string[]
	thirdSpecialization?: string[]
	fourthSpecialization?: string[]
	languageCode?: string
	localeCountryCode?: string
}

export async function updateAgent(
	agentId: number,
	payload: UpdateAgentRequest
): Promise<{ message: string }> {
	try {
		const { data } = await api.put<{ message: string }>(
			`/agents/${agentId}`,
			payload
		)
		return data
	} catch (error) {
		throw new Error(`Error updating agent information: ${error}`)
	}
}

export type UploadPhotoResponse = {
	success: boolean
	message: string
	fileName: string
	fullPath: string
}

export async function uploadAgentPhoto(
	file: File
): Promise<UploadPhotoResponse> {
	const formData = new FormData()
	formData.append("photo", file)

	const { data } = await api.post<{ data: UploadPhotoResponse }>(
		"/userProfile/upload-photo",
		formData,
		{
			headers: { "Content-Type": "multipart/form-data" }
		}
	)

	return data.data
}

export type SendEmailOTPResponse = {
	token: string
	success: boolean
}

export async function sendEmailOTP(
	email: string,
	errorMessage?: string
): Promise<SendEmailOTPResponse> {
	try {
		const { data } = await api.post("/agents/send-notification", {
			recipient: email,
			channel: OTPChannel.EMAIL_OTP,
			purpose: OTPPurpose.EMAIL_VERIFICATION_OTP
		})

		return {
			token: data?.data?.token ?? "",
			success: data?.data?.success ?? false
		}
	} catch (error) {
		// Check for specific EMAIL_ALREADY_EXISTS error
		if (
			isAxiosError(error) &&
			error.response?.data?.message?.errorCode === "EMAIL_ALREADY_EXISTS"
		) {
			throw new Error(errorMessage)
		}

		return { token: "", success: false }
	}
}

export interface LegalAcceptanceRequest {
	acceptedTerms: {
		policyType: string
		policyVersion: string
		policyLanguage: string
	}[]
}

export interface LegalAcceptanceResponse {
	success: boolean
	message: string
	acceptedTerms: {
		policyType: string
		policyVersion: string
		policyLanguage: string
	}[]
}

export async function submitLegalAcceptance(
	acceptanceData: LegalAcceptanceRequest
): Promise<LegalAcceptanceResponse> {
	try {
		const { data } = await api.post<{ data: LegalAcceptanceResponse }>(
			"/agents/legal-acceptance",
			acceptanceData
		)
		return data.data
	} catch (error) {
		console.error("Error submitting legal acceptance:", error)
		throw error
	}
}

export async function getLegalAcceptance(): Promise<LegalAcceptanceResponse> {
	try {
		const { data } = await api.get<{ data: LegalAcceptanceResponse }>(
			"/agents/legal-acceptance"
		)
		return data.data
	} catch (error) {
		console.error("Error getting legal acceptance:", error)
		throw error
	}
}

export async function addAgencyAffiliation(
	agencyId: number
): Promise<{ success: boolean; message: string }> {
	try {
		const { data } = await api.post<{ success: boolean; message: string }>(
			"/agents/agency-affiliation",
			{ agencyId }
		)
		return data
	} catch (err) {
		const msg = isAxiosError(err)
			? (err.response?.data?.message?.message ?? "Failed to add affiliation.")
			: "Failed to add affiliation."
		throw new Error(msg)
	}
}

export async function setActiveAgencyApi(
	agencyId: number
): Promise<{ success: boolean; message: string }> {
	try {
		const { data } = await api.put<{ success: boolean; message: string }>(
			"/agents/active-agency",
			{ agencyId }
		)
		return data
	} catch (err) {
		const msg = isAxiosError(err)
			? (err.response?.data?.message?.message ?? "Failed to set active agency.")
			: "Failed to set active agency."
		throw new Error(msg)
	}
}

export async function setDefaultAgencyApi(
	agencyId: number
): Promise<{ success: boolean; message: string }> {
	try {
		const { data } = await api.put<{ success: boolean; message: string }>(
			"/agents/default-agency",
			{ agencyId }
		)
		return data
	} catch (err) {
		const msg = isAxiosError(err)
			? (err.response?.data?.message?.message ??
				"Failed to set default agency.")
			: "Failed to set default agency."
		throw new Error(msg)
	}
}
